import { useState, useCallback, useMemo } from 'react';
import { Layout, Menu, Button, Typography, Avatar, Dropdown, theme } from 'antd';
import {
  DashboardOutlined,
  InboxOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  ToolOutlined,
  AuditOutlined,
  DollarOutlined,
  SettingOutlined,
  LogoutOutlined,
  UserOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BookOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  FileTextOutlined,
  ContactsOutlined,
  DashboardFilled,
  SafetyCertificateOutlined,
  AlertOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';
import { useAuth } from '../stores/AuthContext';
import UsersPage from '@modules/admin/UsersPage';
import AuditLogPage from '@modules/admin/AuditLogPage';
import WmsPage from '@modules/warehouse/WmsPage';
import UnitsOfMeasurePage from '@modules/dictionaries/UnitsOfMeasurePage';
import ProductsPage from '@modules/dictionaries/ProductsPage';
import ProductEditPage from '@modules/dictionaries/ProductEditPage';
import SuppliersPage from '@modules/purchasing/SuppliersPage';
import PurchaseOrdersPage from '@modules/purchasing/PurchaseOrdersPage';
import PurchaseOrderEditPage from '@modules/purchasing/PurchaseOrderEditPage';
import PurchaseOrderDetailPage from '@modules/purchasing/PurchaseOrderDetailPage';
import CustomersPage from '@modules/sales/CustomersPage';
import SalesOrdersPage from '@modules/sales/SalesOrdersPage';
import SalesOrderEditPage from '@modules/sales/SalesOrderEditPage';
import SalesOrderDetailPage from '@modules/sales/SalesOrderDetailPage';
import InvoicesPage from '@modules/finance/InvoicesPage';
import InvoiceDetailPage from '@modules/finance/InvoiceDetailPage';
import ArDashboardPage from '@modules/finance/ArDashboardPage';
import ErrorBoundary from './ErrorBoundary';
import InstalledEquipmentPage from '@modules/service/InstalledEquipmentPage';
import ServiceRequestsPage from '@modules/service/ServiceRequestsPage';
import ServiceRequestDetailPage from '@modules/service/ServiceRequestDetailPage';
import SlaDashboardPage from '@modules/service/SlaDashboardPage';
import RegulatoryDocumentsPage from '@modules/regulatory/RegulatoryDocumentsPage';
import RecallsPage from '@modules/regulatory/RecallsPage';
import RecallDetailPage from '@modules/regulatory/RecallDetailPage';
import VigilancePage from '@modules/regulatory/VigilancePage';
import OwnerDashboardPage from '@modules/dashboard/OwnerDashboardPage';
import SettingsPage from '@modules/settings/SettingsPage';
import ExchangeRateWidget from './ExchangeRateWidget';
import type { MenuProps } from 'antd';
import { mdFilterMenuByRoles } from './menuAccess';

const { Header, Sider, Content } = Layout;

function mdBuildMenuItems(mdUserRoles: string[]): MenuProps['items'] {
  const mdIsAdmin = mdUserRoles.includes('Admin');
  const mdSettingsChildren: MenuProps['items'] = [
    { key: 'settings-profile', icon: <UserOutlined />, label: 'Профіль' },
  ];
  if (mdIsAdmin) {
    mdSettingsChildren.push(
      { key: 'settings-users', icon: <TeamOutlined />, label: 'Користувачі' },
      { key: 'settings-audit', icon: <AuditOutlined />, label: 'Журнал аудиту' },
    );
  }

  const mdItems: MenuProps['items'] = [
    { key: 'dashboard', icon: <DashboardOutlined />, label: 'Дашборд' },
    { key: 'warehouse', icon: <InboxOutlined />, label: 'Склад (WMS)' },
    {
      key: 'dictionaries',
      icon: <BookOutlined />,
      label: 'Довідники',
      children: [
        { key: 'dict-products', icon: <AppstoreOutlined />, label: 'Номенклатура' },
        { key: 'dict-units', icon: <UnorderedListOutlined />, label: 'Одиниці виміру' },
        { key: 'dict-suppliers', icon: <ContactsOutlined />, label: 'Постачальники' },
        { key: 'dict-customers', icon: <TeamOutlined />, label: 'Клієнти' },
      ],
    },
    {
      key: 'purchases',
      icon: <ShoppingCartOutlined />,
      label: 'Закупівлі',
      children: [
        { key: 'purch-orders', icon: <FileTextOutlined />, label: 'Замовлення постачальникам' },
      ],
    },
    {
      key: 'sales',
      icon: <DollarOutlined />,
      label: 'Продажі',
      children: [
        { key: 'sales-orders', icon: <FileTextOutlined />, label: 'Замовлення клієнтів' },
      ],
    },
    {
      key: 'finance',
      icon: <AuditOutlined />,
      label: 'Фінанси',
      children: [
        { key: 'fin-invoices', icon: <FileTextOutlined />, label: 'Рахунки' },
        { key: 'fin-ar', icon: <DollarOutlined />, label: 'Дебіторська заборгованість' },
      ],
    },
    {
      key: 'service',
      icon: <ToolOutlined />,
      label: 'Сервіс',
      children: [
        { key: 'svc-equipment', icon: <AppstoreOutlined />, label: 'Встановлене обладнання' },
        { key: 'svc-requests', icon: <FileTextOutlined />, label: 'Сервісні заявки' },
        { key: 'svc-sla', icon: <DashboardFilled />, label: 'SLA Dashboard' },
      ],
    },
    {
      key: 'regulatory',
      icon: <SafetyCertificateOutlined />,
      label: 'Регуляторний',
      children: [
        { key: 'reg-documents', icon: <FileTextOutlined />, label: 'Документи' },
        { key: 'reg-recalls', icon: <AlertOutlined />, label: 'Recalls' },
        { key: 'reg-vigilance', icon: <ExperimentOutlined />, label: 'Vigilance' },
      ],
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Налаштування',
      children: mdSettingsChildren,
    },
  ];

  return mdFilterMenuByRoles(mdItems, mdUserRoles);
}

type SubPage =
  | { type: 'none' }
  | { type: 'product-edit'; productId?: string }
  | { type: 'po-edit'; orderId?: string }
  | { type: 'po-detail'; orderId: string }
  | { type: 'so-edit'; orderId?: string }
  | { type: 'so-detail'; orderId: string }
  | { type: 'inv-detail'; invoiceId: string }
  | { type: 'sr-detail'; requestId: string }
  | { type: 'recall-detail'; recallId: string };

export default function MainLayout() {
  const [mdCollapsed, setMdCollapsed] = useState(false);
  const [mdCurrentModule, setMdCurrentModule] = useState('dashboard');
  const [mdSubPage, setMdSubPage] = useState<SubPage>({ type: 'none' });
  const { user, logout } = useAuth();
  const { token: mdThemeToken } = theme.useToken();
  const mdMenuItems = useMemo(
    () => mdBuildMenuItems(user?.roles ?? []),
    [user?.roles]
  );

  const mdUserMenuItems: MenuProps['items'] = [
    { key: 'profile', icon: <UserOutlined />, label: 'Профіль' },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Вийти', danger: true },
  ];

  const mdHandleUserMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'logout') logout();
  };

  const mdHandleMenuClick: MenuProps['onClick'] = ({ key }) => {
    setMdCurrentModule(key);
    setMdSubPage({ type: 'none' });
  };

  const mdHandleEditProduct = useCallback((mdProductId: string) => {
    setMdSubPage({ type: 'product-edit', productId: mdProductId });
  }, []);

  const mdHandleCreateProduct = useCallback(() => {
    setMdSubPage({ type: 'product-edit' });
  }, []);

  const mdHandleBackToProducts = useCallback(() => {
    setMdSubPage({ type: 'none' });
  }, []);

  const mdHandleCreateOrder = useCallback(() => {
    setMdSubPage({ type: 'po-edit' });
  }, []);

  const mdHandleViewOrder = useCallback((mdOrderId: string) => {
    setMdSubPage({ type: 'po-detail', orderId: mdOrderId });
  }, []);

  const mdHandleEditOrder = useCallback((mdOrderId: string) => {
    setMdSubPage({ type: 'po-edit', orderId: mdOrderId });
  }, []);

  const mdHandleBackToOrders = useCallback(() => {
    setMdSubPage({ type: 'none' });
  }, []);

  // ── Sales Order handlers ──────────────────────────────────
  const mdHandleCreateSalesOrder = useCallback(() => {
    setMdSubPage({ type: 'so-edit' });
  }, []);

  const mdHandleViewSalesOrder = useCallback((mdOrderId: string) => {
    setMdSubPage({ type: 'so-detail', orderId: mdOrderId });
  }, []);

  const mdHandleEditSalesOrder = useCallback((mdOrderId: string) => {
    setMdSubPage({ type: 'so-edit', orderId: mdOrderId });
  }, []);

  const mdHandleBackToSalesOrders = useCallback(() => {
    setMdSubPage({ type: 'none' });
  }, []);

  // ── Finance handlers ──────────────────────────────────────
  // Рахунок створює модалка (зі списку рахунків або із замовлення) — тут лише відкриваємо його картку
  const mdHandleInvoiceCreated = useCallback((mdInvoiceId: string) => {
    setMdCurrentModule('fin-invoices');
    setMdSubPage({ type: 'inv-detail', invoiceId: mdInvoiceId });
  }, []);

  const mdHandleViewInvoice = useCallback((mdInvoiceId: string) => {
    setMdSubPage({ type: 'inv-detail', invoiceId: mdInvoiceId });
  }, []);

  const mdHandleBackToInvoices = useCallback(() => {
    setMdSubPage({ type: 'none' });
  }, []);

  // ── Service handlers ────────────────────────────────────
  // Заявку створює модалка на сторінці обладнання — тут лише відкриваємо її картку
  const mdHandleServiceRequestCreated = useCallback((mdRequestId: string) => {
    setMdCurrentModule('svc-requests');
    setMdSubPage({ type: 'sr-detail', requestId: mdRequestId });
  }, []);

  const mdHandleViewServiceRequest = useCallback((mdRequestId: string) => {
    setMdSubPage({ type: 'sr-detail', requestId: mdRequestId });
  }, []);

  const mdHandleBackToServiceRequests = useCallback(() => {
    setMdSubPage({ type: 'none' });
  }, []);

  // ── Regulatory handlers ─────────────────────────────────
  const mdHandleViewRecall = useCallback((mdRecallId: string) => {
    setMdSubPage({ type: 'recall-detail', recallId: mdRecallId });
  }, []);

  // Recall створює модалка на сторінці recalls — тут лише відкриваємо його картку
  const mdHandleRecallCreated = useCallback((mdRecallId: string) => {
    setMdSubPage({ type: 'recall-detail', recallId: mdRecallId });
  }, []);

  const mdHandleBackToRecalls = useCallback(() => {
    setMdSubPage({ type: 'none' });
  }, []);

  const mdGetSelectedKeys = (): string[] => {
    return [mdCurrentModule];
  };

  const mdGetOpenKeys = (): string[] => {
    if (mdCurrentModule.startsWith('dict-')) return ['dictionaries'];
    if (mdCurrentModule.startsWith('purch-')) return ['purchases'];
    if (mdCurrentModule.startsWith('sales-')) return ['sales'];
    if (mdCurrentModule.startsWith('fin-')) return ['finance'];
    if (mdCurrentModule.startsWith('svc-')) return ['service'];
    if (mdCurrentModule.startsWith('reg-')) return ['regulatory'];
    if (mdCurrentModule.startsWith('settings')) return ['settings'];
    return [];
  };

  const mdRenderContent = () => {
    // Handle sub-pages first
    if (mdCurrentModule === 'dict-products' && mdSubPage.type === 'product-edit') {
      return (
        <ProductEditPage
          productId={mdSubPage.productId}
          onBack={mdHandleBackToProducts}
        />
      );
    }

    if (mdCurrentModule === 'purch-orders' && mdSubPage.type === 'po-edit') {
      return (
        <PurchaseOrderEditPage
          orderId={mdSubPage.orderId}
          onBack={mdHandleBackToOrders}
        />
      );
    }

    if (mdCurrentModule === 'purch-orders' && mdSubPage.type === 'po-detail') {
      return (
        <PurchaseOrderDetailPage
          orderId={mdSubPage.orderId}
          onBack={mdHandleBackToOrders}
          onNavigateToReceipt={() => {
            setMdCurrentModule('warehouse');
            setMdSubPage({ type: 'none' });
          }}
        />
      );
    }

    // Sales sub-pages
    if (mdCurrentModule === 'sales-orders' && mdSubPage.type === 'so-edit') {
      return (
        <SalesOrderEditPage
          orderId={mdSubPage.orderId}
          onBack={mdHandleBackToSalesOrders}
        />
      );
    }

    if (mdCurrentModule === 'sales-orders' && mdSubPage.type === 'so-detail') {
      return (
        <SalesOrderDetailPage
          orderId={mdSubPage.orderId}
          onBack={mdHandleBackToSalesOrders}
          onInvoiceCreated={mdHandleInvoiceCreated}
        />
      );
    }

    // Service sub-pages
    if (mdCurrentModule === 'svc-requests' && mdSubPage.type === 'sr-detail') {
      return (
        <ServiceRequestDetailPage
          requestId={mdSubPage.requestId}
          onBack={mdHandleBackToServiceRequests}
        />
      );
    }

    // Regulatory sub-pages
    if (mdCurrentModule === 'reg-recalls' && mdSubPage.type === 'recall-detail') {
      return (
        <RecallDetailPage
          recallId={mdSubPage.recallId}
          onBack={mdHandleBackToRecalls}
        />
      );
    }

    // Finance sub-pages
    if (mdCurrentModule === 'fin-invoices' && mdSubPage.type === 'inv-detail') {
      return (
        <InvoiceDetailPage
          invoiceId={mdSubPage.invoiceId}
          onBack={mdHandleBackToInvoices}
        />
      );
    }

    // Main pages
    switch (mdCurrentModule) {
      case 'dashboard':
        return (
          <OwnerDashboardPage
            onNavigate={(mdModule) => {
              setMdCurrentModule(mdModule);
              setMdSubPage({ type: 'none' });
            }}
          />
        );
      case 'warehouse':
        return <WmsPage />;
      case 'dict-units':
        return <UnitsOfMeasurePage />;
      case 'dict-products':
        return (
          <ProductsPage
            onEditProduct={mdHandleEditProduct}
            onCreateProduct={mdHandleCreateProduct}
          />
        );
      case 'dict-suppliers':
        return <SuppliersPage />;
      case 'purch-orders':
        return (
          <PurchaseOrdersPage
            onCreateOrder={mdHandleCreateOrder}
            onViewOrder={mdHandleViewOrder}
            onEditOrder={mdHandleEditOrder}
          />
        );
      case 'dict-customers':
        return <CustomersPage />;
      case 'sales-orders':
        return (
          <SalesOrdersPage
            onCreateOrder={mdHandleCreateSalesOrder}
            onViewOrder={mdHandleViewSalesOrder}
            onEditOrder={mdHandleEditSalesOrder}
          />
        );
      case 'fin-invoices':
        return (
          <InvoicesPage
            onInvoiceCreated={mdHandleInvoiceCreated}
            onViewInvoice={mdHandleViewInvoice}
          />
        );
      case 'fin-ar':
        return <ArDashboardPage />;
      case 'svc-equipment':
        return (
          <InstalledEquipmentPage
            onServiceRequestCreated={mdHandleServiceRequestCreated}
          />
        );
      case 'svc-requests':
        return (
          <ServiceRequestsPage
            onViewRequest={mdHandleViewServiceRequest}
          />
        );
      case 'svc-sla':
        return <SlaDashboardPage />;
      case 'reg-documents':
        return <RegulatoryDocumentsPage />;
      case 'reg-recalls':
        return (
          <RecallsPage
            onViewRecall={mdHandleViewRecall}
            onRecallCreated={mdHandleRecallCreated}
          />
        );
      case 'reg-vigilance':
        return <VigilancePage />;
      case 'settings-profile':
        return <SettingsPage />;
      case 'settings-users':
        return <UsersPage />;
      case 'settings-audit':
        return <AuditLogPage />;
      default: {
        const mdFindLabel = (mdItems: MenuProps['items'], mdKey: string): string | null => {
          if (!mdItems) return null;
          for (const mdItem of mdItems) {
            if (!mdItem) continue;
            if ('key' in mdItem && mdItem.key === mdKey) {
              return (mdItem as { label: string }).label;
            }
            if ('children' in mdItem && mdItem.children) {
              const mdFound = mdFindLabel(mdItem.children, mdKey);
              if (mdFound) return mdFound;
            }
          }
          return null;
        };

        const mdLabel = mdFindLabel(mdMenuItems, mdCurrentModule);

        return (
          <>
            <Typography.Title level={4}>
              {mdLabel ?? mdCurrentModule}
            </Typography.Title>
            <Typography.Text type="secondary">
              Модуль у розробці
            </Typography.Text>
          </>
        );
      }
    }
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider trigger={null} collapsible collapsed={mdCollapsed} theme="light">
        <div style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: `1px solid ${mdThemeToken.colorBorderSecondary}`
        }}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {mdCollapsed ? 'MD' : 'MedDevice'}
          </Typography.Title>
        </div>
        <Menu
          mode="inline"
          selectedKeys={mdGetSelectedKeys()}
          defaultOpenKeys={mdGetOpenKeys()}
          items={mdMenuItems}
          onClick={mdHandleMenuClick}
          style={{ borderRight: 0 }}
        />
      </Sider>
      <Layout>
        <Header style={{
          padding: '0 24px',
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${mdThemeToken.colorBorderSecondary}`
        }}>
          <Button
            type="text"
            icon={mdCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setMdCollapsed(!mdCollapsed)}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ExchangeRateWidget />
            <Dropdown menu={{ items: mdUserMenuItems, onClick: mdHandleUserMenuClick }} placement="bottomRight">
              <Button type="text" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Avatar icon={<UserOutlined />} size="small" />
                <span>{user?.firstName} {user?.lastName}</span>
              </Button>
            </Dropdown>
          </div>
        </Header>
        <Content style={{ margin: 24, padding: 24, background: '#fff', borderRadius: 8 }}>
          {/* Помилка однієї сторінки не повинна прибирати меню: ключ скидає запобіжник при навігації */}
          <ErrorBoundary resetKey={`${mdCurrentModule}:${JSON.stringify(mdSubPage)}`}>
            {mdRenderContent()}
          </ErrorBoundary>
        </Content>
      </Layout>
    </Layout>
  );
}
